import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faPen } from '@fortawesome/free-solid-svg-icons';
import './ShippingInfoForm.css';
import { saveShippingInfo } from '../../../store/checkoutSlice';

// ✅ Variable de entorno
const API_URL = import.meta.env.VITE_API_URL;

const fieldLabels = {
  nombre: 'Nombre',
  email: 'Correo',
  telefono: 'Teléfono',
  direccion: 'Dirección',
  ciudad: 'Ciudad',
  departamento: 'Departamento',
  zip: 'Código Postal'
};

const ShippingInfoForm = ({ formData, setFormData, setIsShippingInfoValid }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedData, setSavedData] = useState(null);
  const dispatch = useDispatch();
  const formRef = useRef(null);

  // Obtener el user_id del estado global
  const userId = useSelector(state => state.auth.user?.id);
  console.log('userId desde redux', userId);

  useEffect(() => {
    if (!userId) return;  // Espera a que userId esté disponible
  
    axios.get(`${API_URL}/checkout/shipping`, {
      params: { userId }  // envía userId como query param
    })
      .then(res => {
        if (res.status === 200 && res.data && Object.keys(res.data).length > 0) {
          const hasMeaningfulData = ['nombre', 'email', 'telefono', 'direccion'].some(
            key => res.data[key] && res.data[key].trim() !== ''
          );
          if (hasMeaningfulData) {
            setSavedData(res.data);
            setFormData(res.data);
            dispatch(saveShippingInfo(res.data));
          }
        }
      })
      .catch(err => {
        console.error('Error cargando datos de envío:', err);
      });
  }, [userId, setFormData, dispatch]);
  

  useEffect(() => {
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const isValid = Object.values(formData).every(value => value && value.trim() !== '');
    setIsShippingInfoValid(isValid);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    const form = formRef.current;
    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        userId: userId
      };

      const res = await axios.post(`${API_URL}/checkout/shipping`, payload);
      if (res.status === 200 || res.status === 201) {
        setSavedData(formData);
        dispatch(saveShippingInfo(formData));
      } else {
        setError('No se pudo guardar la información.');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Error de servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setSavedData(null);
    if (formRef.current) {
      formRef.current.classList.remove('was-validated');
    }
  };

  const shippingSummary = () => (
    <div className="bg-light rounded-4 shadow-sm p-4 border">
      <div className="d-flex align-items-center justify-content-center text-success fw-semibold mb-3">
        <FontAwesomeIcon icon={faTruck} className="me-2" /> Dirección Guardada
      </div>
      <div className="text-center">
        {Object.entries(savedData).map(([key, val]) => (
          <p key={key} className="mb-1">
            <strong>{fieldLabels[key] || key}:</strong> {val}
          </p>
        ))}
        <button className="btn btn-outline-success btn-sm mt-3" onClick={handleEdit}>
          <FontAwesomeIcon icon={faPen} className="me-1" /> Editar Información
        </button>
      </div>
    </div>
  );

  const shippingForm = () => (
    <div className="bg-light rounded-4 shadow-sm p-4 border">
      <div className="d-flex align-items-center text-success fw-semibold mb-3">
        <FontAwesomeIcon icon={faTruck} className="me-2" /> Información de Envío
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <form ref={formRef} noValidate onSubmit={handleSubmit} className="needs-validation">
        <div className="row g-3">
          {['nombre', 'email', 'telefono', 'direccion', 'ciudad', 'departamento', 'zip'].map(field => (
            <div
              key={field}
              className={`col-md-${field === 'ciudad' || field === 'departamento' || field === 'zip' ? '4' : '6'}`}
            >
              <label className="form-label">{fieldLabels[field]}</label>
              <input
                type={field === 'email' ? 'email' : 'text'}
                className="form-control form-control-sm"
                name={field}
                value={formData[field]}
                onChange={handleChange}
                required
              />
              <div className="invalid-feedback">{`${fieldLabels[field]} requerido.`}</div>
            </div>
          ))}
        </div>
        <div className="text-end mt-4">
          <button type="submit" className="btn btn-success btn-sm" disabled={loading}>
            {loading ? (
              <>
                <div className="spinner-border spinner-border-sm me-1" role="status" /> Guardando...
              </>
            ) : (
              <>
                <i className="bi bi-save me-1"></i>
                {savedData ? 'Actualizar Datos' : 'Guardar Datos'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="container my-5 px-3">
      {savedData ? shippingSummary() : shippingForm()}
    </div>
  );
};

export default ShippingInfoForm;
